/**
 * @file Flight type definitions and helper functions
 * @description Defines the Flight model with manual override support for baggage and gate
 */

import { serverDate } from '../services/serverClock.js';

/**
 * @typedef {Object} OverrideField
 * @property {string|null} value - The override value (e.g., "Belt 3", "Gate B12")
 * @property {"AUTO"|"MANUAL"} source - Source of the override
 * @property {string} updatedAt - ISO datetime when override was last updated
 * @property {string|null} [expiresAt] - Optional ISO datetime when override expires
 * @property {string|null} [updatedByUserId] - User ID who set the override
 */

/**
 * @typedef {Object} Aircraft
 * @property {string} [type] - Aircraft type
 * @property {string} [registration] - Aircraft registration
 */

/**
 * @typedef {Object} Route
 * @property {string} [origin] - Origin airport code
 * @property {string} [destination] - Destination airport code
 */

/**
 * @typedef {Object} Flight
 * @property {string} _id - MongoDB document ID
 * @property {string} uid - Unique deterministic flight key
 * @property {string} airport - Airport code (e.g., "YQB")
 * @property {"A"|"D"} direction - Direction: "A" for Arrival, "D" for Departure
 * @property {string} [airline] - Airline code
 * @property {string} [flightNumber] - Flight number
 * @property {string} [scheduledTime] - ISO datetime for scheduled time (STA/STD)
 * @property {string} [estimatedTime] - ISO datetime for estimated time
 * @property {string} [actualTime] - ISO datetime for actual time
 * @property {string} [status] - Flight status
 * @property {string|null} [gate] - Automatic gate assignment (for departures)
 * @property {string|null} [baggage] - Automatic baggage carousel/belt (for arrivals)
 * @property {string} [terminal] - Terminal
 * @property {Aircraft} [aircraft] - Aircraft information
 * @property {Route} [route] - Route information (origin/destination)
 * @property {OverrideField} [arrivalBaggageOverride] - Manual override for arrival baggage
 * @property {OverrideField} [departureGateOverride] - Manual override for departure gate
 * @property {OverrideField} [statusOverride] - Manual override for flight status
 * @property {Object} [raw] - Raw FLIFO payload
 * @property {string} createdAt - ISO datetime when flight was created
 * @property {string} updatedAt - ISO datetime when flight was last updated
 */

/**
 * Get the display value for baggage/carousel (for arrival flights)
 * Prefers manual override if valid and not expired, otherwise falls back to automatic value
 *
 * @param {Flight} flight - The flight object
 * @returns {string} The baggage/carousel value to display
 */
export function getDisplayBaggage(flight) {
  if (!flight) return '';

  const now = serverDate(); // server clock: kiosk device clocks drift

  // Prefer manual arrival baggage override for arrivals
  const override = flight.arrivalBaggageOverride;
  if (flight.direction === 'A' && override) {
    const isExpired =
      override.expiresAt && new Date(override.expiresAt) < now;

    if (!isExpired && override.value) {
      return override.value;
    }
  }

  // Fallback to automatic baggage if available
  return flight.baggage || '';
}

/**
 * Get the display value for gate (for departure flights)
 * Prefers manual override if valid and not expired, otherwise falls back to automatic value
 *
 * @param {Flight} flight - The flight object
 * @returns {string} The gate value to display
 */
export function getDisplayGate(flight) {
  if (!flight) return '';

  const now = serverDate(); // server clock: kiosk device clocks drift

  // Prefer manual gate override for departures
  const override = flight.departureGateOverride;
  if (flight.direction === 'D' && override) {
    const isExpired =
      override.expiresAt && new Date(override.expiresAt) < now;

    if (!isExpired && override.value) {
      return override.value;
    }
  }

  // Fallback to automatic gate
  return flight.gate || '';
}

/**
 * Get the source label for baggage (AUTO or MANUAL)
 *
 * @param {Flight} flight - The flight object
 * @returns {"AUTO"|"MANUAL"|""} The source of the baggage value
 */
export function getBaggageSource(flight) {
  if (!flight) return '';

  const now = serverDate(); // server clock: kiosk device clocks drift
  const override = flight.arrivalBaggageOverride;

  if (flight.direction === 'A' && override) {
    const isExpired =
      override.expiresAt && new Date(override.expiresAt) < now;

    if (!isExpired && override.value) {
      return 'MANUAL';
    }
  }

  return flight.baggage ? 'AUTO' : '';
}

/**
 * Get the source label for gate (AUTO or MANUAL)
 *
 * @param {Flight} flight - The flight object
 * @returns {"AUTO"|"MANUAL"|""} The source of the gate value
 */
export function getGateSource(flight) {
  if (!flight) return '';

  const now = serverDate(); // server clock: kiosk device clocks drift
  const override = flight.departureGateOverride;

  if (flight.direction === 'D' && override) {
    const isExpired =
      override.expiresAt && new Date(override.expiresAt) < now;

    if (!isExpired && override.value) {
      return 'MANUAL';
    }
  }

  return flight.gate ? 'AUTO' : '';
}

/**
 * Check if a flight has an active manual baggage override
 *
 * @param {Flight} flight - The flight object
 * @returns {boolean} True if flight has active manual baggage override
 */
export function hasActiveBaggageOverride(flight) {
  if (!flight || flight.direction !== 'A') return false;

  const now = serverDate(); // server clock: kiosk device clocks drift
  const override = flight.arrivalBaggageOverride;

  if (!override || !override.value) return false;

  const isExpired =
    override.expiresAt && new Date(override.expiresAt) < now;

  return !isExpired;
}

/**
 * Check if a flight has an active manual gate override
 *
 * @param {Flight} flight - The flight object
 * @returns {boolean} True if flight has active manual gate override
 */
export function hasActiveGateOverride(flight) {
  if (!flight || flight.direction !== 'D') return false;

  const now = serverDate(); // server clock: kiosk device clocks drift
  const override = flight.departureGateOverride;

  if (!override || !override.value) return false;

  const isExpired =
    override.expiresAt && new Date(override.expiresAt) < now;

  return !isExpired;
}

/**
 * Get the display value for status (for both arrival and departure flights)
 * Prefers manual override if present, otherwise falls back to automatic value
 * No expiration — status overrides are cleared by incoming SITA updates
 *
 * @param {Flight} flight - The flight object
 * @returns {string} The status value to display
 */
export function getDisplayStatus(flight) {
  if (!flight) return '';

  const override = flight.statusOverride;
  if (override && override.value) {
    return override.value;
  }

  return flight.status || '';
}

/**
 * Get the source label for status (AUTO or MANUAL)
 *
 * @param {Flight} flight - The flight object
 * @returns {"AUTO"|"MANUAL"|""} The source of the status value
 */
export function getStatusSource(flight) {
  if (!flight) return '';

  const override = flight.statusOverride;
  if (override && override.value) {
    return 'MANUAL';
  }

  return flight.status ? 'AUTO' : '';
}

/**
 * Check if a flight has an active manual status override
 *
 * @param {Flight} flight - The flight object
 * @returns {boolean} True if flight has active manual status override
 */
export function hasActiveStatusOverride(flight) {
  if (!flight) return false;
  const override = flight.statusOverride;
  return !!(override && override.value);
}
